import * as Q from "q";
import TcmService = require("TestManagement/Scripts/TFS.TestManagement.Service");
import TCMContracts = require("TFS/TestManagement/Contracts");
import WIT_Contracts = require("TFS/WorkItemTracking/Contracts");
import WIT_WebApi = require("TFS/WorkItemTracking/RestClient");
import * as Artifacts_Constants from "VSS/Artifacts/Constants";
import * as Diag from "VSS/Diag";
import { getClient, getService } from "VSS/Service";
import Utils_Array = require("VSS/Utils/Array");
import VSS_Artifacts_Services = require("VSS/Artifacts/Services");
import { TestManagementMigrationService } from "TestManagement/Scripts/TestManagementMigrationService";

let LinkingUtilities = VSS_Artifacts_Services.LinkingUtilities;

const ArtifactLink = "ArtifactLink";

export class LinkedItemsGridViewSource {

    public loadAssociatedWorkItems(projectId: string, testCaseResult: TCMContracts.TestCaseResult): IPromise<WIT_Contracts.WorkItem[]> {

        let deferred = Q.defer<WIT_Contracts.WorkItem[]>();
        let workItemIds: number[] = [];
        let uniqueWorkItemIds: number[] = [];
        let workItem: WIT_Contracts.WorkItem[] = [];
        let tcmservice: TcmService.ITestResultsService2 = TcmService.ServiceManager.instance().testResultsService2();

        let testCaseId: number = testCaseResult.testCase.id ? parseInt(testCaseResult.testCase.id) : 0;
                                                                                                
        let getAssociatedRecentWorkItems = tcmservice.getRecentWorkItems(testCaseResult.automatedTestName, testCaseId);  //getting test reference level workitemss
        let getAssociatedResultWorkItems = tcmservice.getAssociatedWorkItems(parseInt(testCaseResult.testRun.id), testCaseResult.id);  //getting workitems associated with run id

        Q.allSettled([getAssociatedRecentWorkItems, getAssociatedResultWorkItems]).then((results) => {
            results.forEach((p) => {
                if (p.state === "fulfilled") {
                    p.value.forEach((workItem) => {
                        workItemIds.push(parseInt(workItem.id));
                    });
                } else {
                    Diag.logWarning("Unable to load workitems");
                    Diag.logWarning(p.reason);
                }
            });

            uniqueWorkItemIds = Utils_Array.unique(workItemIds).reverse();  //select unique ids as we can have common associated workitems from ref and results level.
            if (uniqueWorkItemIds.length > 0) {
                this.getWorkItemTrackingClient()
                    .getWorkItems(uniqueWorkItemIds, null, null, WIT_Contracts.WorkItemExpand.Relations, null, projectId)
                    .then(
                        (workItems: WIT_Contracts.WorkItem[]) => {
                            deferred.resolve(workItems);
                        },
                        (error) => {
                            Diag.logWarning("Unable to load workitems");
                            Diag.logWarning(error);
                            deferred.reject(null);
                        });
            } else {
                deferred.resolve(workItem);
            }

        },
            (error) => {
                Diag.logWarning("Unable to load workitems");
                Diag.logWarning(error);
                deferred.reject(null);
            });

        return deferred.promise;
    }

    public getUpdatedWorkItem(workItem: WIT_Contracts.WorkItem): IPromise<WIT_Contracts.WorkItem> {
        let deferred = Q.defer<WIT_Contracts.WorkItem>();
        this.getWorkItemTrackingClient().getWorkItem(workItem.id, null, null, WIT_Contracts.WorkItemExpand.Relations).then((updatedWorkItem: WIT_Contracts.WorkItem) => {
            deferred.resolve(updatedWorkItem);
        }, (error) => {
            Diag.logWarning(error);
            deferred.reject(null);
        });
        return deferred.promise;
    }

    public deleteAssociations(testCaseResult: TCMContracts.TestCaseResult, workItems: WIT_Contracts.WorkItem[]): IPromise<WIT_Contracts.WorkItem[]> {
        let count = 0;
        let deferred = Q.defer<WIT_Contracts.WorkItem[]>();
        let deletedWorkItems: WIT_Contracts.WorkItem[] = [];
        workItems.forEach((workItem, index) => {
            let data = [];
            let relationIndicesToRemove: number[] = this._getRelationsIndicesToDelete(testCaseResult, workItem);
            for (let i = 0; i < relationIndicesToRemove.length; ++i) {
                data.push({
                    op: "remove",
                    path: "/relations/" + relationIndicesToRemove[i].toString()
                });
            }

            //check if workitem is attached in automated tests runs hub
            if(testCaseResult.automatedTestName && relationIndicesToRemove.length == 0)
            {
                //called when workitem is linked from release pipeline results tab
                TcmService.ServiceManager.instance().testResultsService().deleteTestMethodToWorkItemLink(testCaseResult.automatedTestName, workItem.id).then((associationDeleted: boolean) => {
                    if (associationDeleted) {
                        deletedWorkItems.push(workItem);
                    }
                    count++;
                    if (count === workItems.length) {
                        deferred.resolve(deletedWorkItems);
                    }
                }, (error) => {
                    count++;
                    if (count === workItems.length) {
                        deferred.resolve(deletedWorkItems);
                    }
                    Diag.logWarning(error);
                });
            }
            else
            {
                // called when workitem is getting linked from manual test plans runs hub.
                this.getWorkItemTrackingClient().updateWorkItem(data, workItem.id).then((WorkItem: WIT_Contracts.WorkItem) => {
                    deletedWorkItems.push(workItem);
                    count++;
                    if (count === workItems.length) {
                        deferred.resolve(deletedWorkItems);
                    }
                }, (error) => {
                    count++;
                    if (count === workItems.length) {
                        deferred.resolve(deletedWorkItems);
                    }
                    Diag.logWarning(error);
                });
            }    
        });

        return deferred.promise;
    }

    private _getRelationsIndicesToDelete(testCaseResult: TCMContracts.TestCaseResult, workItem: WIT_Contracts.WorkItem): number[] {
        let indicesToDelete = [];
        let uniqueId: string = testCaseResult.testRun.id + "." + testCaseResult.id.toString();
        /* We will delete the links to:
           1. TestResult (url of type: ../TcmResult/{RunId}.{ResultId})
           1. TestCaseRef (url of type: ../TcmTest/{TestCaseRefId})
        */
        for (let i = 0; i < workItem.relations.length; ++i) {
            if (workItem.relations[i].rel === ArtifactLink) {
                let decodedRelationUri = LinkingUtilities.decodeUri(workItem.relations[i].url);
                if ((decodedRelationUri.type === Artifacts_Constants.ArtifactTypeNames.TcmResult && decodedRelationUri.id === uniqueId)
                    || decodedRelationUri.type === Artifacts_Constants.ArtifactTypeNames.TcmTest &&  this._isMatch(decodedRelationUri.id, testCaseResult.testCaseReferenceId)) {
                    indicesToDelete.push(i);
                }
            }
        }
        return indicesToDelete;
    }

    private _isMatch(toolSpecificId: string, testCaseReferenceId: number): boolean {
        const service = getService(TestManagementMigrationService);
        return service.getEncodedRefId(TCMContracts.Service.Tcm, testCaseReferenceId) === toolSpecificId ||
            service.getEncodedRefId(TCMContracts.Service.Tfs, testCaseReferenceId) === toolSpecificId;
    }

    private getWorkItemTrackingClient(): WIT_WebApi.WorkItemTrackingHttpClient {
        return getClient<WIT_WebApi.WorkItemTrackingHttpClient>(WIT_WebApi.WorkItemTrackingHttpClient);
    }
}
